import { isNil } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { grey10, grey75 } from '@zocdoc/branding/colors';

import { Expand, MapPin } from '../icons';
import LazyLoad from '../LazyLoad';
import { FontFamily } from '../typography';
import { getUnthemedQueryParams, getThemedQueryParams } from './GenerateStylerQueryParams';

const mapZoom = '12';
const googleMapsApiKey = 'AIzaSyD1cGNhJz2BiG4oODjDAkfOH__dxXC_N10';
const dataProdGuid = '87d1707d';

const StyledMapPin = styled(MapPin).attrs({ 'data-test': 'google-static-map-pin' })`
    font-size: 24px;
    position: absolute;
    left: calc(50% - ${MapPin.WIDTH / 2}em);
    top: calc(50% - 0.5em);
`;

const StyledLargeMapPin = styled(StyledMapPin).attrs({ 'data-test': 'google-static-map-pin-large' })`
    font-size: 42px;
`;

const StyledExpand = styled(Expand).attrs({ 'data-test': 'google-static-map-expand' })`
    font-size: 14px;
    position: absolute;
    bottom: 4px;
    right: 4px;
    vertical-align: bottom;
`;

const StyledLargeExpand = styled(StyledExpand).attrs({ 'data-test': 'google-static-map-expand-large' })`
    font-size: 18px;
    bottom: 10px;
    right: 10px;
`;

const StyledWrapper = styled.div`
    align-items: center;
    color: ${p => p.theme.components.GoogleStaticMap.markerColor};
    cursor: ${p => p.clickable ? 'pointer' : 'normal'};
    display: inline-flex;
    justify-content: center;
    overflow: hidden;
    position: relative;
    background-color: ${grey10};
`;

const MapUnavailable = ({ width, height }) => (
    <svg
        height={height}
        viewBox="0 0 120 120"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <g
            fill={grey10}
            fillRule="evenodd"
        >
            <text
                fill={grey75}
                fontFamily={FontFamily.SharpSansBold}
                fontSize="15"
                fontWeight="500"
                letterSpacing=".12"
            >
                <tspan
                    x="43.965"
                    y="45"
                >map
                </tspan>
                <tspan
                    x="19.74"
                    y="65"
                >unavailable
                </tspan>
            </text>
        </g>
    </svg>
);

class GoogleStaticMap extends PureComponent {
    static defaultProps = {
        shouldLazyLoad: true,
    };

    static propTypes = {
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        onClick: PropTypes.func,

        /**
          * Lazy load could cause problems in IE/Safari if you're trying to load before any scroll/resize action)
          * Defaults to true
          */
        shouldLazyLoad: PropTypes.bool,

        /**
         * This should be provided as a prop, probably via the withTheme() HOC from styled-components.
         */
        theme: PropTypes.shape({
            components: PropTypes.shape({
                GoogleStaticMap: PropTypes.shape({
                    markerColor: PropTypes.string.isRequired,
                    useZocdocMapStyle: PropTypes.bool,
                }).isRequired,
            }).isRequired,
        }).isRequired,
    };

    mapIsSmall() {
        return this.props.height < 100 || this.props.width < 100;
    }

    maybeRenderExpandIcon() {
        if (this.props.onClick) {
            const ExpandComponent = this.mapIsSmall() ? StyledExpand : StyledLargeExpand;
            return (
                <ExpandComponent />
            );
        }
    }

    renderMapPin() {
        const PinComponent = this.mapIsSmall() ? StyledMapPin : StyledLargeMapPin;
        return (
            <PinComponent color="currentColor" />
        );
    }

    maybeLazyLoad = img => {
        const {
            height,
            shouldLazyLoad,
            width,
        } = this.props;
        return shouldLazyLoad ? (
            <LazyLoad
                data-test="google-static-map-lazyload"
                height={`${height}px`}
                width={`${width}px`}
            >
                {img}
            </LazyLoad>
        ) : img;
    }

    render() {
        const {
            latitude,
            longitude,
            width,
            height,
            onClick,
            shouldLazyLoad,
            theme: { components: { GoogleStaticMap: { useZocdocMapStyle } } },
            ...props
        } = this.props;

        const googleMapStylers = useZocdocMapStyle ? getThemedQueryParams() : getUnthemedQueryParams();
        const src = `https://api.zocdoc.com/maps/v1/static?center=${latitude},${longitude}&zoom=${mapZoom}&size=${width}x${height}${googleMapStylers}&key=${googleMapsApiKey}`;

        if (!isNil(latitude) && !isNil(longitude)) {
            const img = (
                <img
                    alt=""
                    aria-hidden="true"
                    data-test="google-static-map-image"
                    height={height}
                    src={src}
                    width={width}
                />
            );

            return (
                <StyledWrapper
                    {...props}
                    clickable={!!onClick}
                    data-prod-guids={`google-static-map:[${dataProdGuid}]`}
                    data-test="google-static-map"
                    onClick={onClick}
                >
                    {this.maybeLazyLoad(img)}
                    {this.renderMapPin()}
                    {this.maybeRenderExpandIcon()}
                </StyledWrapper>
            );
        } else {
            return (
                <StyledWrapper
                    {...props}
                    data-test="google-static-map-unavailable"
                >
                    <MapUnavailable
                        height={height}
                        width={width}
                    />
                </StyledWrapper>
            );
        }
    }
}

export {
    GoogleStaticMap as default,
    GoogleStaticMap,
};
